import { motion, AnimatePresence, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Filter, ArrowDown } from "lucide-react";
import KOPipelineStep from "./KOPipelineStep";
import { allKeywords } from "./mockData";

const KOAffordabilityFilter = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [filtered, setFiltered] = useState(false);

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => setFiltered(true), 800);
      return () => clearTimeout(timer);
    }
  }, [isInView]);

  const displayKeywords = allKeywords.map((kw) => ({
    ...kw,
    id: kw.keyword,
  }));

  const visibleKeywords = filtered
    ? displayKeywords.filter((kw) => kw.affordable)
    : displayKeywords;

  return (
    <section className="py-24 px-4 bg-background" ref={sectionRef}>
      <div className="container mx-auto max-w-6xl">
        <KOPipelineStep step={2} title="Filter Out Keywords You Can't Afford">
          {/* Counter */}
          <motion.div
            className="flex items-center gap-6 mb-8 flex-wrap"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 bg-white rounded-full px-5 py-2.5 border border-border shadow-sm">
              <Filter className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium">
                {filtered ? (
                  <>
                    <span className="text-red-500 line-through mr-2">26 keywords</span>
                    <ArrowDown className="inline h-4 w-4 text-muted-foreground mx-1" />
                    <span className="text-green-600 font-bold">18 affordable keywords</span>
                  </>
                ) : (
                  <span>26 keyword candidates</span>
                )}
              </span>
            </div>
            {filtered && (
              <motion.span
                className="text-sm text-red-500 font-medium"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
              >
                8 keywords removed (bid &gt; $15 CPA target)
              </motion.span>
            )}
          </motion.div>

          {/* Keyword pills with AnimatePresence */}
          <div className="bg-white rounded-xl p-8 border border-border shadow-sm min-h-[200px]">
            <div className="flex flex-wrap gap-3">
              <AnimatePresence mode="popLayout">
                {visibleKeywords.map((kw) => (
                  <motion.div
                    key={kw.id}
                    layout
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{
                      opacity: 0,
                      scale: 0.3,
                      y: -30,
                      rotate: -10,
                      transition: { duration: 0.4 },
                    }}
                    transition={{
                      layout: { type: "spring", stiffness: 300, damping: 30 },
                      opacity: { duration: 0.3 },
                    }}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${
                      kw.affordable
                        ? "bg-green-50 text-green-800 border-green-200"
                        : "bg-red-50 text-red-700 border-red-200"
                    }`}
                  >
                    <span>{kw.keyword}</span>
                    <span
                      className={`text-xs font-bold ${
                        kw.affordable ? "text-green-600" : "text-red-500"
                      }`}
                    >
                      ${kw.bidEstimate.toFixed(2)}
                    </span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* Legend */}
          <div className="flex gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-muted-foreground">Affordable (CPC &lt; $15 CPA)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-muted-foreground">Too expensive (CPC &gt; $15 CPA)</span>
            </div>
          </div>

          {/* SEO text */}
          <motion.p
            className="mt-8 text-muted-foreground leading-relaxed max-w-4xl"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            This is the critical step that most advertisers skip entirely. By removing keywords where
            the cost-per-click exceeds your target CPA, you guarantee that every remaining keyword has
            the mathematical potential to be profitable. The expensive keywords that get filtered out are
            often the most "obvious" choices — broad terms like "roofing company" or "best roofer" that
            dominate traditional keyword research. But for budget-conscious advertisers, these keywords
            are traps that consume your entire daily spend on a single click.
          </motion.p>
        </KOPipelineStep>
      </div>
    </section>
  );
};

export default KOAffordabilityFilter;
